import { trimSearch } from '@xmatters/common/utils/searchUtil';
import {
  INCIDENT_SEVERITIES,
  INCIDENT_STATUSES,
  SERVICE_OWNER_RELATIONSHIP,
} from 'xm-constants/services';

const START = 'START';
const SUCCESS = 'SUCCESS';
const FAIL = 'FAIL';

const baseUri = '/api/xm/1/services';

const CREATE_SERVICE = 'CREATE_SERVICE';
export const CREATE_SERVICE_START = `${CREATE_SERVICE}_${START}`;
export const CREATE_SERVICE_SUCCESS = `${CREATE_SERVICE}_${SUCCESS}`;
export const CREATE_SERVICE_FAIL = `${CREATE_SERVICE}_${FAIL}`;
const FETCH_SERVICES = 'FETCH_SERVICES';
export const FETCH_SERVICES_START = `${FETCH_SERVICES}_${START}`;
export const FETCH_SERVICES_SUCCESS = `${FETCH_SERVICES}_${SUCCESS}`;
export const FETCH_SERVICES_FAIL = `${FETCH_SERVICES}_${FAIL}`;
const DELETE_SERVICE = 'DELETE_SERVICE';
export const DELETE_SERVICE_START = `${DELETE_SERVICE}_${START}`;
export const DELETE_SERVICE_SUCCESS = `${DELETE_SERVICE}_${SUCCESS}`;
export const DELETE_SERVICE_FAIL = `${DELETE_SERVICE}_${FAIL}`;
export const TOGGLE_SERVICE_SELECTED = 'TOGGLE_SERVICE_SELECTED';
export const CLEAR_SELECTION = 'CLEAR_SELECTION';
export const SET_ADD_SERVICE_ERROR = 'SET_ADD_SERVICE_ERROR';
export const CLEAR_ADD_SERVICE_ERROR = 'CLEAR_ADD_SERVICE_ERROR';
export const UPDATE_SERVICE_IN_SERVICES_LIST =
  'UPDATE_SERVICE_IN_SERVICES_LIST';
const SERVICE_SEARCH_GROUPS = 'SERVICE_SEARCH_GROUPS';
export const SERVICE_SEARCH_GROUPS_START = 'SERVICE_SEARCH_GROUPS_START';
export const SERVICE_SEARCH_GROUPS_SUCCESS = 'SERVICE_SEARCH_GROUPS_SUCCESS';
export const SERVICE_SEARCH_GROUPS_FAIL = 'SERVICE_SEARCH_GROUPS_FAIL';
export const SERVICE_RESET_SEARCH_GROUPS = 'SERVICE_RESET_SEARCH_GROUPS';
export const SERVICE_SET_SELECTED_GROUPS = 'SERVICE_SET_SELECTED_GROUPS';
export const SERVICE_UPDATE_FILTER = 'SERVICE_UPDATE_FILTER';

const { IN_PROGRESS, OPEN } = INCIDENT_STATUSES;
const { MINIMAL, LOW, MEDIUM, HIGH, CRITICAL } = INCIDENT_SEVERITIES;
const { MEMBER, NOT_OWNED, SUPERVISOR } = SERVICE_OWNER_RELATIONSHIP;

export const fetchServices = ({
  owner,
  search,
  severity,
  sortBy,
  sortOrder,
  uri = baseUri,
  replaceExisting = true,
  loadMore = false,
  serviceTiers,
  serviceTypes,
}) => {
  const trimmedSearchText = trimSearch(search);
  const searchParams =
    trimmedSearchText.length >= 2
      ? {
          search: trimmedSearchText,
          operand: 'AND',
          fields: ['NAME', 'DESCRIPTION'],
        }
      : {};
  const incidentParams = {};
  if (severity && severity !== MINIMAL) {
    incidentParams['incidents.status'] = [OPEN, IN_PROGRESS];
    incidentParams['incidents.severity'] = [];
    // @see COR-37899
    // The break statements are removed intentionally.
    // The reason for this is that a user selects a single radio button
    // but that button represents its value and all values above it.
    // The menu items read:
    //   * Show All
    //   * Critical
    //   * High Severity and Higher
    //   * Medium Severity and Higher
    //   * Low Severity and Higher
    /* eslint-disable default-case, no-fallthrough */
    switch (severity) {
      case LOW:
        incidentParams['incidents.severity'].push(LOW);
      case MEDIUM:
        incidentParams['incidents.severity'].push(MEDIUM);
      case HIGH:
        incidentParams['incidents.severity'].push(HIGH);
      case CRITICAL:
        incidentParams['incidents.severity'].push(CRITICAL);
    }
  }
  const ownerParams = {};
  const { selected = [] } = owner || {};
  if (selected.length === 1 && selected[0].isDefault) {
    if (selected[0].value === MEMBER) {
      ownerParams['ownedBy.members'] = [owner.loggedInUserId];
    }
    if (selected[0].value === SUPERVISOR) {
      ownerParams['ownedBy.supervisors'] = [owner.loggedInUserId];
    }
    if (selected[0].value === NOT_OWNED) {
      ownerParams['ownedBy.exists'] = false;
    }
  } else if (selected.length > 0) {
    ownerParams.ownedBy = selected.map(item => item.value);
  }
  const embedParams = loadMore
    ? {}
    : {
        include: 'metrics',
        embed: 'permittedActions',
      };
  const tiersParams = {};
  if (serviceTiers) {
    tiersParams.serviceTier = serviceTiers;
  }
  const typesParam = {};
  if (serviceTypes) {
    typesParam.serviceType = serviceTypes;
  }
  return {
    type: FETCH_SERVICES,
    meta: {
      xmApi: true,
      method: 'get',
      uri,
      isUriEncoded: loadMore,
      params: {
        ...embedParams,
        ...searchParams,
        ...tiersParams,
        ...typesParam,
        sortBy,
        sortOrder,
        ...incidentParams,
        ...ownerParams,
      },
    },
    replaceExisting,
  };
};

export const createService = (targetName, description, ownedBy = null) => ({
  type: CREATE_SERVICE,
  meta: {
    xmApi: true,
    method: 'post',
    uri: baseUri,
    params: {
      targetName,
      description,
      ownedBy,
    },
  },
});

export const deleteServices = ({ serviceList }) => {
  // this is the maximum number of xmApi responses we will wait for
  // i.e. we will DELETE /service/serviceId for 20 services, wait for all of those to resolve, then do the next 20
  const MAX_AWAITING_PROMISES = 20;
  return dispatch => {
    const handleNext = list => {
      const toDelete = list.slice(0, MAX_AWAITING_PROMISES);
      return Promise.all(
        toDelete.map(async s => {
          await dispatch({
            type: DELETE_SERVICE,
            meta: {
              xmApi: true,
              method: 'del',
              uri: `${baseUri}/${s.id}`,
            },
            service: s,
          });
        }),
      ).then(() => {
        if (list.length > MAX_AWAITING_PROMISES) {
          return handleNext(list.slice(MAX_AWAITING_PROMISES, list.length));
        }
        return Promise.resolve();
      });
    };
    return handleNext(serviceList);
  };
};

export const loadMore = ({ uri, filter }) => {
  return fetchServices({
    uri,
    ...filter,
    replaceExisting: false,
    loadMore: true,
  });
};

export const toggleServiceSelected = id => ({
  type: TOGGLE_SERVICE_SELECTED,
  id,
});

export const clearSelection = () => ({
  type: CLEAR_SELECTION,
});

export const clearAddServiceError = () => ({
  type: CLEAR_ADD_SERVICE_ERROR,
});

export const setAddServiceError = err => ({
  type: SET_ADD_SERVICE_ERROR,
  err,
});

/**
 * Update a specific Service in the redux-store service list cache
 * @param {*} service The service (including required id) to update in redux store
 * @returns Redux action creator
 */
export const updateServiceInList = service => ({
  type: UPDATE_SERVICE_IN_SERVICES_LIST,
  service,
});

export const searchGroups = dispatch => (endpoint, search) => {
  const params = {};
  params.limit = 100;

  const trimmedSearchText = trimSearch(search);
  if (trimmedSearchText.length >= 2) {
    params.search = trimmedSearchText;
    params.operand = 'AND';
    params.fields = ['NAME'];
  }

  return dispatch({
    type: SERVICE_SEARCH_GROUPS,
    meta: {
      xmApi: true,
      method: 'get',
      uri: `/webapi/xm/1/${endpoint}`,
      params,
    },
  });
};

export const resetDynamicFilters = dispatch => (parameter, items) => {
  return dispatch({
    type: SERVICE_RESET_SEARCH_GROUPS,
    payload: { parameter, items },
  });
};

export const setActiveDynamicFilters = dispatch => (parameter, items) => {
  return dispatch({
    type: SERVICE_SET_SELECTED_GROUPS,
    payload: { parameter, items },
  });
};

export const updateFilter = () => ({
  type: SERVICE_UPDATE_FILTER,
});
